<template>
  <a-config-provider :locale="locale">
    <div class="layout wh100 flex-column over-hidden">
      <!-- 头部 -->
      <div
        class="header w100 flex-row-end-between"
        v-if="route.path !== '/login'"
      >
        <div
          class="left flex-row-end-start pl20"
          v-if="route.path !== '/dashboard'"
        >
          <i class="iconfont icon-zuo arrow" @click="router.back()"></i>
          <!-- <LocaleSwitch class="pl20"/> -->
        </div>
        <div class="left flex-row-end-start pl20" v-else>
          <!-- <LocaleSwitch /> -->
          <!-- <img src="@/assets/images/weather.png" alt="" />
          <i>多云</i>
          <span>32</span>
          <em class="mb10">℃</em>
          <div class="flex-center">优26</div> -->
        </div>

        <div class="mid h100 flex-row-start-center pt20">
          <h2 class="head-text" :class="{ fr: currentLocale !== 'zh-CN' }">
            {{ t("app.title") }}
          </h2>
        </div>
        <div class="right flex-row-center-end pr20">
          <a-tooltip
            v-for="menu in filteredMenus"
            :key="menu.name"
            :title="menu.title"
          >
            <component
              :is="menu.icon"
              class="mr10 menu-icon"
              @click="handleMenuClick(menu)"
            />
          </a-tooltip>
          <a-tooltip :title="t('nav.logout')">
            <LogoutOutlined class="mr10 menu-icon" @click="handleLogout" />
          </a-tooltip>
        </div>
      </div>
      <!-- 主体 -->
      <div class="container w100 over-hidden">
        <router-view></router-view>
      </div>
      <!-- 告警弹窗 -->
      <AlarmBox></AlarmBox>
      <AcPointAlarmBox></AcPointAlarmBox>
    </div>
  </a-config-provider>
</template>

<script>
import {
  defineAsyncComponent,
  defineComponent,
  onBeforeMount,
  h,
  ref,
  watch,
  computed,
} from "vue";
import zhCN from "ant-design-vue/es/locale/zh_CN";
import enUS from "ant-design-vue/es/locale/en_US";
import frFR from "ant-design-vue/es/locale/fr_FR";
import { currentLocale, t } from "@/utils/i18n";
import { logout } from "@/api/user";
import commonMixin from "@/mixins/common";

import {
  HomeOutlined,
  LogoutOutlined,
  LineChartOutlined,
  FundViewOutlined,
  DesktopOutlined,
  BellOutlined,
  UserOutlined,
  CodeOutlined,
  ToolOutlined,
  GlobalOutlined,
} from "@ant-design/icons-vue";

const AlarmBox = defineAsyncComponent(() =>
  import("./components/AlarmBox.vue")
);
const AcPointAlarmBox = defineAsyncComponent(() =>
  import("./components/AcPointAlarmBox.vue")
);

export default defineComponent({
  // 已启用类型推断
  name: "Root",
  components: {
    AlarmBox,
    AcPointAlarmBox,
    HomeOutlined,
    LogoutOutlined,
    FundViewOutlined,
    LineChartOutlined,
    DesktopOutlined,
    BellOutlined,
    UserOutlined,
    CodeOutlined,
    ToolOutlined,
    GlobalOutlined,
  },
  setup() {
    const locale = ref(zhCN);
    const mapAntdLocale = { "zh-CN": zhCN, "en-US": enUS, "fr-FR": frFR };
    watch(
      () => currentLocale.value,
      (lang) => {
        locale.value = mapAntdLocale[lang] || zhCN;
      },
      { immediate: true }
    );
    let { route, router, store } = commonMixin();

    const currentUser = ref(sessionStorage.getItem("user") || "");

    const filteredMenus = computed(() => {
      // 使用当前页面的协议、主机名和端口构建URL
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;
      const baseUrl = port
        ? `${protocol}//${hostname}:${port}`
        : `${protocol}//${hostname}`;

      const allMenus = [
        {
          name: "dashboard",
          title: t("nav.dashboard"),
          icon: h(HomeOutlined),
          path: "dashboard",
        },
        {
          name: "graph",
          title: t("nav.graph"),
          icon: h(FundViewOutlined),
          path: "graph",
          replace: true,
        },
        {
          name: "device",
          title: t("nav.device"),
          icon: h(DesktopOutlined),
          path: "device",
        },
        {
          name: "record",
          title: t("nav.record"),
          icon: h(BellOutlined),
          path: "record",
        },
        {
          name: "user",
          title: t("nav.user"),
          icon: h(UserOutlined),
          path: "user",
          roles: ["admin1"],
        },
        {
          name: "protocol",
          title: t("nav.protocol"),
          icon: h(CodeOutlined),
          path: "protocol",
          roles: ["admin1"],
        },
        // {
        //   name: "manage",
        //   title: t("nav.manage"),
        //   icon: h(ToolOutlined),
        //   type: "iframe",
        //   url: `${baseUrl}/manage/?hideHeader=1&lang=en`,
        // },
        // {
        //   name: "hes",
        //   title: "HES",
        //   icon: h(GlobalOutlined),
        //   type: "iframe",
        //   url: `${baseUrl}/tcmr/main/index.action`,
        // },
      ];

      return allMenus.filter((menu) => {
        if (!menu.roles) return true;
        return menu.roles.includes(currentUser.value);
      });
    });

    const handleMenuClick = (menu) => {
      if (menu.type === "iframe" && menu.url) {
        // 网页嵌入类型，跳转到 iframe 页面并传递 url
        router.push({
          name: "iframe",
          query: { url: encodeURIComponent(menu.url) },
        });
      } else {
        // 普通页面跳转
        if (menu.replace) {
          router.replace({ name: menu.path });
        } else {
          router.push({ name: menu.path });
        }
      }
    };

    watch(
      () => route.path,
      (newPath) => {
        currentUser.value = sessionStorage.getItem("user") || "";
        // 当从登录页面跳转到其他页面且已登录时，连接 Socket
        if (
          newPath !== "/login" &&
          sessionStorage.getItem("user") &&
          !store.state.socketConnected
        ) {
          store.dispatch("subscribeData");
        }
      }
    );

    async function handleLogout() {
      // 断开 WebSocket 连接并重置连接状态
      store.dispatch("unsubscribeData");
      // 调用退出登录接口
      await logout();
      sessionStorage.clear();
      router.replace({ name: "login" });
    }

    onBeforeMount(() => {
      //
    });

    return {
      locale,
      route,
      router,
      handleLogout,
      t,
      currentUser,
      filteredMenus,
      handleMenuClick,
    };
  },
});
</script>

<style lang="less" scoped>
#root {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  overflow: auto;

  /*定义滚动条高宽及背景
 高宽分别对应横竖滚动条的尺寸*/
  &:deep(::-webkit-scrollbar) {
    width: 4px;
    height: 4px;
    border-radius: 2px;
  }

  /*定义滚动条轨道
 内阴影+圆角*/
  &:deep(::-webkit-scrollbar-track) {
    background: transparent;
  }

  /*定义滑块
 内阴影+圆角*/
  &:deep(::-webkit-scrollbar-thumb) {
    border-radius: 2px;
    background: #8afff7;
  }
}
.layout {
  color: #fff;
  background: url("~@/assets/images/page-bg.png") no-repeat center / 100% 100%;
  .header {
    height: 7.2rem;
    max-height: 7.2rem;
    background: url("~@/assets/images/header-bg.png") 0 0 no-repeat;
    background-size: 100% 100%;
    .left {
      width: 30%;
      padding-left: 2.5rem;
      // img {
      //   width: 2.5rem;
      //   height: 2.5rem;
      // }
      // i {
      //   font-size: 0.75rem;
      //   color: #d8d8d8;
      //   white-space: nowrap;
      //   margin-bottom: 5px;
      // }
      // span {
      //   font-size: 2rem;
      //   margin: 0 4px;
      //   line-height: 2.75rem;
      // }
      // em {
      //   font-size: 18px;
      //   line-height: 1.5rem;
      // }
      // div {
      //   padding: 0 8px;
      //   height: 1.125rem;
      //   font-size: 0.75rem;
      //   color: #181818;
      //   white-space: nowrap;
      //   margin-left: 5px;
      //   border-radius: 0.5rem;
      //   background: linear-gradient(270deg, #336de7 0%, #29cbe8 100%);
      // }
      .arrow {
        color: #00baad !important;
        font-size: 2.25rem !important;
      }
    }
    .mid {
      // margin-top: 2.5rem;
      > img {
        width: 31rem;
      }
      .head-text {
        text-align: center;
        font-size: 2rem;
        background: linear-gradient(to bottom, #b6ffd1, #139e95);
        color: transparent;
        -webkit-background-clip: text;
        font-family: SF Pro SC, SF Pro Display, SF Pro Icons, PingFang SC,
          Helvetica Neue, Helvetica, Arial, sans-serif;
        .fr {
          font-size: 0.2rem;
        }
      }
    }
    .right {
      width: 30%;
      .menu-icon {
        font-size: 2rem;
        margin-bottom: 5px;
        cursor: pointer;
      }
      > span {
        font-size: 0.875rem;
        font-weight: 500;
        color: #33afb3;
        white-space: nowrap;
        // &:nth-child(2) {
        //   font-size: 1.5rem;
        //   color: #d8d8d8;
        // }
      }
    }
  }
  .container {
    height: 100%;
    padding: 1rem;
  }
}
</style>
